import { useCallback, useEffect, useMemo, useState } from "react";
import { Title, useNotify } from "react-admin";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Link as RouterLink } from "react-router-dom";

import { EMPLOYEE_TYPE } from "@/constants";
import { callApi } from "@/dataprovider/misc-apis";
import { formatIsoMonth } from "@/utils/date-format";
import { staffShowPath } from "@/containers/staff/staff/staff-show-helpers";

import {
  attendanceCellSx,
  attendanceOffDaySx,
  formatAttendanceCellLabel,
  formatAttendanceHoursLabel,
  isExceptionRow,
  isOffWeekday,
} from "./attendance-cell-display";
import { exportAttendanceCsv } from "./attendance-csv";
import { computeAttendanceFooterStats, formatPresentRatePercent } from "./attendance-footer-stats";
import {
  dayColumnWidth,
  estimateGridMinWidth,
  HOURS_COL_WIDTH,
  NAME_COL_WIDTH,
  SUMMARY_COL_WIDTH,
  weekdayLetterFromIndex,
  type DayMeta,
} from "./attendance-grid-layout";
import {
  filterAndSortAttendanceRows,
  nextNameSortDirection,
  type AttendanceSortDirection,
} from "./attendance-sort-filter";
import type {
  AttendanceDensity,
  AttendanceEmployeeRow,
  AttendanceMonthResponse,
  AttendanceSortKey,
  DayDrilldown,
} from "./attendance-types";
import { AttendanceDayDrawer } from "./components/AttendanceDayDrawer";
import { AttendanceLegend } from "./components/AttendanceLegend";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/** Sentinel for department filter — matches API `employeeTypeWhere` ALL handling. */
const DEPARTMENT_ALL = "ALL";

function stickyLeftSx(left: number, zIndex: number) {
  return (theme: Theme) => ({
    position: "sticky" as const,
    left,
    zIndex,
    bgcolor: "background.paper",
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  });
}

function stickyRightSx(right: number, zIndex: number, opts?: { showLeftEdge?: boolean }) {
  return (theme: Theme) => ({
    position: "sticky" as const,
    right,
    zIndex,
    bgcolor: "background.paper",
    // Keep full grid shadows when sticky bgcolor would otherwise hide neighbors' edges
    boxShadow: opts?.showLeftEdge
      ? `inset 1px 0 0 ${theme.palette.divider}, inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`
      : `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  });
}

/** Vertical + bottom grid lines via inset shadow (survives fills / last-row MUI resets / overflow clip). */
function cellGridSx(theme: Theme) {
  return {
    border: "none",
    boxShadow: `inset -1px 0 0 ${theme.palette.divider}, inset 0 -1px 0 ${theme.palette.divider}`,
  };
}

function SummaryHead({
  label,
  title,
  right,
  width,
  zIndex,
  showLeftEdge,
}: {
  label: string;
  title: string;
  right: number;
  width: number;
  zIndex: number;
  showLeftEdge?: boolean;
}) {
  return (
    <TableCell
      align="center"
      sx={[
        stickyRightSx(right, zIndex, { showLeftEdge }),
        {
          minWidth: width,
          width,
          top: 0,
          px: 0.25,
        },
      ]}
    >
      <Tooltip title={title}>
        <Box component="span" sx={{ fontWeight: 700, cursor: "help" }}>
          {label}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

const StaffAttendanceList = () => {
  const notify = useNotify();
  const [month, setMonth] = useState(formatIsoMonth(dayjs()));
  const [type, setType] = useState<string>(DEPARTMENT_ALL);
  const [rows, setRows] = useState<AttendanceEmployeeRow[]>([]);
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [offWeekdays, setOffWeekdays] = useState<number[]>([0]);
  const [lateCheckInLocal, setLateCheckInLocal] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<AttendanceDensity>("compact");
  const [sortKey, setSortKey] = useState<AttendanceSortKey>("name");
  const [nameDirection, setNameDirection] = useState<AttendanceSortDirection>("asc");
  const [nameQuery, setNameQuery] = useState("");
  const [drilldown, setDrilldown] = useState<DayDrilldown | null>(null);

  const daysInMonth = useMemo(() => {
    const end = dayjs(`${month}-01`).daysInMonth();
    const days: number[] = [];
    for (let d = 1; d <= end; d += 1) days.push(d);
    return days;
  }, [month]);

  const includeLate = Boolean(lateCheckInLocal);
  const dayWidth = dayColumnWidth(density);

  const summaryRightOffsets = useMemo(() => {
    // From right edge: [Late], Hours, Leave, Absent, Incomplete, Present
    const order: { key: string; width: number }[] = [
      { key: "hours", width: HOURS_COL_WIDTH },
      { key: "leave", width: SUMMARY_COL_WIDTH },
      { key: "absent", width: SUMMARY_COL_WIDTH },
      { key: "incomplete", width: SUMMARY_COL_WIDTH },
      { key: "present", width: SUMMARY_COL_WIDTH },
    ];
    if (includeLate) {
      order.splice(0, 0, { key: "late", width: SUMMARY_COL_WIDTH });
    }
    const offsets: Record<string, number> = {};
    let acc = 0;
    for (const col of order) {
      offsets[col.key] = acc;
      acc += col.width;
    }
    return offsets;
  }, [includeLate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: response } = await callApi({
        location: "employeesAttendance",
        data: { month, type },
        method: "GET",
      });
      const body = response as AttendanceMonthResponse;
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTimezone(body?.timezone || "Asia/Kolkata");
      setOffWeekdays(Array.isArray(body?.offWeekdays) ? body.offWeekdays : [0]);
      setLateCheckInLocal(body?.lateCheckInLocal ?? null);
    } catch {
      notify("Failed to load attendance", { type: "error" });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [month, type, notify]);

  useEffect(() => {
    if (month) {
      void fetchData();
    }
  }, [month, type, fetchData]);

  const visibleRows = useMemo(
    () => filterAndSortAttendanceRows(rows, nameQuery, sortKey, nameDirection),
    [rows, nameQuery, sortKey, nameDirection]
  );

  const footerStats = useMemo(() => computeAttendanceFooterStats(visibleRows), [visibleRows]);

  const dayMeta = useMemo(
    (): DayMeta[] =>
      daysInMonth.map((d) => {
        const dateStr = `${month}-${String(d).padStart(2, "0")}`;
        const local = dayjs.tz(dateStr, timezone);
        const weekday = local.isValid() ? local.day() : dayjs(dateStr).day();
        return {
          day: d,
          weekday,
          weekdayLetter: weekdayLetterFromIndex(weekday),
          isOff: isOffWeekday(weekday, offWeekdays),
        };
      }),
    [daysInMonth, month, timezone, offWeekdays]
  );

  const gridMinWidth = useMemo(
    () =>
      estimateGridMinWidth({
        visibleDayCount: dayMeta.length,
        density,
        includeLate,
      }),
    [dayMeta.length, density, includeLate]
  );

  const handleExport = () => {
    if (visibleRows.length === 0) {
      notify("Nothing to export", { type: "warning" });
      return;
    }
    exportAttendanceCsv({
      rows: visibleRows,
      daysInMonth,
      month,
      timezone,
      includeLate,
    });
  };

  const stickyHeadZ = 4;
  const stickyBodyZ = 2;
  const colSpanEmpty = dayMeta.length + 1 + 5 + (includeLate ? 1 : 0);

  return (
    <Box
      sx={{
        px: { xs: 1, sm: 1.5 },
        py: 1.5,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        // Force flex ancestors to honor viewport width instead of table min-content
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: 0,
      }}
    >
      <Title title="Staff Attendance" />

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        mb={1}
        useFlexGap
        sx={{ minWidth: 0, maxWidth: "100%" }}
      >
        <TextField
          label="Month"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          size="small"
          sx={{ width: { xs: "100%", sm: "10.5rem" } }}
        />
        <FormControl size="small" sx={{ width: { xs: "100%", sm: "10.5rem" } }}>
          <InputLabel id="attendance-type-label">Department</InputLabel>
          <Select
            labelId="attendance-type-label"
            value={type}
            onChange={(e) => setType(e.target.value)}
            label="Department"
          >
            <MenuItem value={DEPARTMENT_ALL}>All</MenuItem>
            {Object.entries(EMPLOYEE_TYPE).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Search name / id"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          size="small"
          sx={{ width: { xs: "100%", sm: "12rem" } }}
        />
        <FormControl size="small" sx={{ width: { xs: "100%", sm: "11rem" } }}>
          <InputLabel id="attendance-sort-label">Sort</InputLabel>
          <Select
            labelId="attendance-sort-label"
            value={sortKey === "name" ? "" : sortKey}
            displayEmpty
            onChange={(e) => {
              const next = e.target.value as AttendanceSortKey | "";
              if (next === "") {
                setSortKey("name");
                setNameDirection("asc");
              } else {
                setSortKey(next);
              }
            }}
            label="Sort"
          >
            <MenuItem value="">
              <em>By column</em>
            </MenuItem>
            <MenuItem value="absent">Most absent</MenuItem>
            <MenuItem value="incomplete">Most incomplete</MenuItem>
            <MenuItem value="hours">Fewest hours</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={density}
          onChange={(_e, next) => {
            if (next) setDensity(next as AttendanceDensity);
          }}
          aria-label="Density"
        >
          <ToggleButton value="compact">Compact</ToggleButton>
          <ToggleButton value="times">Times</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton
              color="inherit"
              onClick={() => void fetchData()}
              aria-label="Refresh"
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={loading || visibleRows.length === 0}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <AttendanceLegend />

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        {footerStats.employeeCount} staff · avg present{" "}
        {formatPresentRatePercent(footerStats.avgPresentRate)} · {footerStats.withAbsentCount} with
        ≥1 absent · {footerStats.withIncompleteCount} with ≥1 incomplete ·{" "}
        {formatAttendanceHoursLabel(footerStats.totalHours)} total
        {timezone ? ` · ${timezone}` : ""}
      </Typography>

      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 240px)",
          maxWidth: "100%",
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          position: "relative",
        }}
      >
        {loading ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.7),
              zIndex: 6,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : null}
        <Table
          size="small"
          stickyHeader
          sx={(theme) => ({
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: gridMinWidth,
            tableLayout: "fixed",
            // Keep last-row grid lines clear of the scrollport clip edge
            marginBottom: "1px",
            "& th, & td": {
              ...cellGridSx(theme),
              whiteSpace: "nowrap",
            },
            // Beat MUI's last-row borderBottom: 0
            "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
              ...cellGridSx(theme),
            },
          })}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={sortKey === "name" ? nameDirection : false}
                sx={[
                  stickyLeftSx(0, stickyHeadZ),
                  {
                    minWidth: NAME_COL_WIDTH,
                    width: NAME_COL_WIDTH,
                    top: 0,
                    px: 1,
                  },
                ]}
              >
                <TableSortLabel
                  active={sortKey === "name"}
                  direction={nameDirection}
                  onClick={() => {
                    const nextDir = nextNameSortDirection(sortKey, nameDirection);
                    setSortKey("name");
                    setNameDirection(nextDir);
                  }}
                >
                  Staff
                </TableSortLabel>
              </TableCell>
              {dayMeta.map(({ day, weekdayLetter, isOff }) => (
                <TableCell
                  key={day}
                  align="center"
                  sx={(theme) => ({
                    minWidth: dayWidth,
                    width: dayWidth,
                    top: 0,
                    zIndex: 3,
                    px: 0,
                    py: 0.5,
                    typography: "caption",
                    lineHeight: 1.15,
                    ...(isOff ? attendanceOffDaySx(theme) : {}),
                  })}
                >
                  <Tooltip title={`${weekdayLetter} ${day}`}>
                    <Box component="span">
                      {weekdayLetter}
                      <br />
                      {day}
                    </Box>
                  </Tooltip>
                </TableCell>
              ))}
              <SummaryHead
                label="P"
                title="Present days"
                right={summaryRightOffsets.present}
                width={SUMMARY_COL_WIDTH}
                zIndex={stickyHeadZ}
                showLeftEdge
              />
              <SummaryHead
                label="!"
                title="Incomplete days"
                right={summaryRightOffsets.incomplete}
                width={SUMMARY_COL_WIDTH}
                zIndex={stickyHeadZ}
              />
              <SummaryHead
                label="X"
                title="Absent days"
                right={summaryRightOffsets.absent}
                width={SUMMARY_COL_WIDTH}
                zIndex={stickyHeadZ}
              />
              <SummaryHead
                label="L"
                title="Leave days (pending or approved)"
                right={summaryRightOffsets.leave}
                width={SUMMARY_COL_WIDTH}
                zIndex={stickyHeadZ}
              />
              <SummaryHead
                label="Hrs"
                title="Total hours"
                right={summaryRightOffsets.hours}
                width={HOURS_COL_WIDTH}
                zIndex={stickyHeadZ}
              />
              {includeLate ? (
                <SummaryHead
                  label="Lt"
                  title="Late days"
                  right={summaryRightOffsets.late}
                  width={SUMMARY_COL_WIDTH}
                  zIndex={stickyHeadZ}
                />
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length > 0 ? (
              visibleRows.map((emp) => {
                const exception = isExceptionRow(
                  emp.summary.absentDays,
                  emp.summary.incompleteDays
                );
                return (
                  <TableRow
                    key={emp.employeeId}
                    hover
                    sx={(theme) =>
                      exception ? { bgcolor: alpha(theme.palette.warning.main, 0.06) } : {}
                    }
                  >
                    <TableCell
                      sx={[
                        stickyLeftSx(0, stickyBodyZ),
                        {
                          minWidth: NAME_COL_WIDTH,
                          width: NAME_COL_WIDTH,
                          px: 1,
                          py: 0.5,
                        },
                      ]}
                    >
                      <MuiLink
                        component={RouterLink}
                        to={staffShowPath(emp.employeeId, month)}
                        underline="hover"
                        color="inherit"
                        sx={{
                          display: "block",
                          fontWeight: exception ? 600 : 500,
                          typography: "body2",
                          lineHeight: 1.25,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={emp.name}
                      >
                        {emp.name}
                      </MuiLink>
                      <Typography variant="caption" color="text.secondary" component="div">
                        <MuiLink
                          component={RouterLink}
                          to={staffShowPath(emp.employeeId, month)}
                          underline="hover"
                          color="text.secondary"
                        >
                          #{emp.employeeId}
                        </MuiLink>
                        {emp.department
                          ? ` · ${EMPLOYEE_TYPE[emp.department] ?? emp.department}`
                          : ""}
                      </Typography>
                    </TableCell>
                    {dayMeta.map(({ day, isOff }) => {
                      const cell = emp.days[day];
                      const label = formatAttendanceCellLabel(cell, density, timezone);
                      return (
                        <TableCell
                          key={day}
                          align="center"
                          onClick={() =>
                            setDrilldown({
                              employee: emp,
                              day,
                              cell: cell ?? {
                                status: isOff ? "OFF" : "ABSENT",
                                checkIn: null,
                                checkOut: null,
                                hours: null,
                                punches: [],
                              },
                            })
                          }
                          sx={(theme) => ({
                            cursor: "pointer",
                            minWidth: dayWidth,
                            width: dayWidth,
                            px: density === "times" ? 0.25 : 0,
                            typography: "caption",
                            ...attendanceCellSx(theme, cell, { isOffColumn: isOff }),
                          })}
                        >
                          {label}
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="center"
                      sx={[
                        stickyRightSx(summaryRightOffsets.present, stickyBodyZ, {
                          showLeftEdge: true,
                        }),
                        {
                          minWidth: SUMMARY_COL_WIDTH,
                          width: SUMMARY_COL_WIDTH,
                          px: 0.25,
                          fontWeight: 600,
                        },
                      ]}
                    >
                      {emp.summary.presentDays}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={[
                        stickyRightSx(summaryRightOffsets.incomplete, stickyBodyZ),
                        {
                          minWidth: SUMMARY_COL_WIDTH,
                          width: SUMMARY_COL_WIDTH,
                          px: 0.25,
                        },
                      ]}
                    >
                      {emp.summary.incompleteDays}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={[
                        stickyRightSx(summaryRightOffsets.absent, stickyBodyZ),
                        {
                          minWidth: SUMMARY_COL_WIDTH,
                          width: SUMMARY_COL_WIDTH,
                          px: 0.25,
                        },
                      ]}
                    >
                      {emp.summary.absentDays}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={[
                        stickyRightSx(summaryRightOffsets.leave, stickyBodyZ),
                        {
                          minWidth: SUMMARY_COL_WIDTH,
                          width: SUMMARY_COL_WIDTH,
                          px: 0.25,
                        },
                      ]}
                    >
                      {emp.summary.leaveDays ?? 0}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={[
                        stickyRightSx(summaryRightOffsets.hours, stickyBodyZ),
                        {
                          minWidth: HOURS_COL_WIDTH,
                          width: HOURS_COL_WIDTH,
                          px: 0.25,
                          typography: "caption",
                        },
                      ]}
                    >
                      {formatAttendanceHoursLabel(emp.summary.totalHours)}
                    </TableCell>
                    {includeLate ? (
                      <TableCell
                        align="center"
                        sx={[
                          stickyRightSx(summaryRightOffsets.late, stickyBodyZ),
                          {
                            minWidth: SUMMARY_COL_WIDTH,
                            width: SUMMARY_COL_WIDTH,
                            px: 0.25,
                          },
                        ]}
                      >
                        {emp.summary.lateDays ?? 0}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={colSpanEmpty} align="center">
                  {loading ? "Loading…" : "No attendance data found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AttendanceDayDrawer
        open={drilldown != null}
        drilldown={drilldown}
        month={month}
        timezone={timezone}
        onClose={() => setDrilldown(null)}
      />
    </Box>
  );
};

export default StaffAttendanceList;
